import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useMatch } from "react-router-dom";

import styled from "styled-components";

import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import SelectBox from "components/atoms/input/SelectBox";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import { useBibJsonUpdate } from "hooks/queries/useBibliographies";

import Constants from "utils/constants";
import { getJsonToBib } from "utils/request/bibliography";
import { genericEnterKeyDown } from "utils/validation";

import saveAs from "file-saver";
import { v4 as uuidv4 } from "uuid";

const Container = styled.div`
    width: 100%;
`;

const TabSection = styled.div`
    display: flex;
    align-items: center;
`;

const TabButton = styled.button`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 32px;
    background-color: var(--color-White);
    border: Transparent;

    &:after {
        content: "";
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: ${(props) => (props.active ? "var(--color-Key)" : "var(--color-DisabledText)")};
    }
`;

const TabButtonText = styled(Text).attrs((props) => ({
    variant: props.active ? "body-md-500" : "body-md-400",
    color: props.active ? "var(--color-Key)" : "var(--color-DisabledText)",
}))``;

const TabViewSection = styled.div`
    margin: 16px 0 0 0;
    width: 100%;
    height: 100%;
    overflow: auto;
`;

const FormContainer = styled.form`
    ${(props) => (props.active ? "" : "display: none;")}

    position: relative;
    width: 100%;
`;

const FormInner = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
`;

const FormRow = styled.div`
    display: ${(props) => (props.field === "abstract" ? "none" : "flex")};
    justify-content: initial;
    align-items: flex-start;
    margin: 0 0 8px 0;
    width: 100%;
`;

const ItemLeft = styled.div`
    display: flex;
    align-items: center;
    margin: 0 8px 0 0;
    width: 140px;
    height: 24px;
`;

const ItemRight = styled.div`
    display: flex;
    align-items: flex-start;
    width: calc(100% - 148px);
`;

const LabelSign = styled.div`
    min-width: 2px;
    height: 14px;
    background-color: var(--color-Key);
`;

const FormLabelWrap = styled.div`
    position: relative;
    top: 4px;
    display: flex;
    align-items: flex-start;
    margin: 0 0 8px 0;
    padding: 0 0 0 8px;
    overflow-wrap: anywhere;
`;

const FormLabel = styled.label`
    margin: 0;
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    cursor: text;
`;

const FormContent = styled.div`
    display: flex;
    width: 100%;
`;

const FormInput = styled.input`
    flex: 1;
    margin: 0;
    padding: 0 12px;
    height: 24px;
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    background-color: var(--color-White);
    border: solid 1px ${(props) => (props.hasError ? "var(--color-Warn)" : "var(--color-Outline)")};
    border-radius: 4px;
    box-sizing: border-box;

    &:disabled {
        background-color: var(--color-DisabledInput);
    }
`;

const WithDelBtn = styled.div`
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const FieldAddArea = styled.div`
    margin: 16px 0 0 0;
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    row-gap: 8px;
`;

const FieldAddButton = styled(Button)`
    margin: 0 8px 0 0;
    padding: 0 8px;
    height: 24px;
    width: auto;
    color: var(--color-White);
    font-size: 1.4rem;
    background-color: var(--color-Button3);
    &:hover {
        background-color: var(--color-ButtonHover3);
    }
`;

const ErrorMessage = styled.div`
    position: relative;
    width: calc(100% - 148px);
    left: 148px;
    color: var(--color-Warn);
    height: 18px;
    font-size: 10px;
    font-weight: 400;
    margin-bottom: 8px;
`;

const BibTeXContainer = styled.div`
    ${(props) => (props.active ? "" : "display: none;")}

    position: relative;
    padding: 6px;
    width: 100%;
    background-color: var(--color-CodeBack);
    border: solid 2px var(--color-Black);
    border-radius: 4px;
    overflow: inherit;

    .buttonWrap {
        button {
            visibility: hidden;
            opacity: 0;
        }
    }

    &:hover {
        .buttonWrap {
            right: 16px;

            button {
                visibility: visible;
                opacity: 1;
            }
        }
    }
`;

const BibTexTextarea = styled.textarea`
    width: 100%;
    height: 300px;
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-White);
    background-color: transparent;
    border: transparent;
    outline: none;
    resize: none;

    &::placeholder {
        color: var(--color-DisabledText);
    }

    &::-webkit-scrollbar {
        width: 6px;
        display: initial;
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--color-Black);
    }
`;

const BibTexButtonColumn = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 24px;
    height: 52px;
    transition: all 300ms ease-in-out;
`;

function TabReferenceBibInfo(props, ref) {
    const { setToast } = useToast();
    const match = useMatch("/:space_uuid/*");
    const space_uuid = match?.params?.space_uuid ?? "";

    const [tabActive, setTabActive] = useState("form");

    const { register, setValue, unregister } = useForm();

    const sortBibJson = (bibJson) => {
        // 1. entry_type 2. citation_key, 3. constants, 4. etc 순으로 정렬한다.
        const sortedBibJson = {};
        const copiedBibJson = JSON.parse(JSON.stringify(bibJson));
        const entryType = copiedBibJson.entry_type;
        const citationKey = copiedBibJson.citation_key;

        delete copiedBibJson.entry_type;
        delete copiedBibJson.citation_key;

        sortedBibJson.entry_type = entryType;
        sortedBibJson.citation_key = citationKey;
        if (copiedBibJson.abstract) {
            setValue("abstract", copiedBibJson.abstract);
        }
        // required field가 다음에 쌓이게 변경
        const fieldList = Constants.BIBTEX_FIELD[entryType];
        Object.keys(fieldList).forEach((field) => {
            if (sortedBibJson[field] === undefined && copiedBibJson[field] !== undefined) {
                sortedBibJson[field] = copiedBibJson[field];
            }
        });

        Object.keys(copiedBibJson)
            .sort()
            .forEach((key) => {
                sortedBibJson[key] = copiedBibJson[key];
            });
        return sortedBibJson;
    };
    const [bibJson, setBibJson] = useState(sortBibJson(props.bibliography.bib_json || {}));
    const [deletedField, setDeletedField] = useState([]);
    const bibJsonMutation = useBibJsonUpdate();
    const [error, setError] = useState({});

    useEffect(() => {
        if (Object.keys(bibJson).length !== 0) {
            setDeletedField(
                Object.keys(Constants.BIBTEX_FIELD[bibJson.entry_type]).filter((field) => bibJson[field] === undefined),
            );
        }
    }, [bibJson]);

    const fieldDelete = (fieldName) => {
        const newBibJson = { ...bibJson };
        delete newBibJson[fieldName];
        handleUpdate(newBibJson);
    };

    const handleOnKeyDown = (e) => {
        genericEnterKeyDown(e, () => {
            e.preventDefault();
            e.target.blur();
        });
    };

    const entryTypeChange = (value) => {
        const newBibJson = { ...bibJson };
        newBibJson.entry_type = value;
        const fieldList = Constants.BIBTEX_FIELD[value];
        Object.keys(fieldList).forEach((field) => {
            if (fieldList[field] !== "optional" && !newBibJson[field]) {
                newBibJson[field] = "";
            }
        });
        Object.keys(newBibJson).forEach((field) => {
            if (!fieldList[field] && field !== "entry_type" && newBibJson[field] === "") {
                delete newBibJson[field];
            }
        });
        handleUpdate(newBibJson);
        setError({});
    };

    const bibJsonMutate = (bibJson, options = {}) => {
        bibJsonMutation.mutate(
            {
                id: props.bibliography.id || undefined,
                bib_json: bibJson,
                is_validation_check: true,
            },
            {
                onSuccess: (data, variables, context) => {
                    setError({});
                    if (options.onSuccess) options.onSuccess(data, variables, context);
                },
                onError: (error, variables, context) => {
                    console.log(error.response.data);
                    setError(error.response.data);
                    if (error.response.data.bib_json) {
                        Alert("warn", "에러", error.response.data.bib_json);
                    }
                    if (options.onError) options.onError(error, variables, context);
                },
            },
        );
    };

    const handleUpdate = (changedBibJson) => {
        const sortedBibJson = sortBibJson(changedBibJson);
        setBibJson(sortedBibJson);
    };

    // region: BibTex 탭
    const bibtexTextareaRef = useRef(null);
    const [bibtexValue, setBibtexValue] = useState("");

    useEffect(() => {
        if (tabActive === "bibtex") {
            getJsonToBib(space_uuid, bibJson)
                .then((res) => {
                    if (res.bib) {
                        setBibtexValue(res.bib);
                    } else {
                        Alert("warn", "에러", "BibTeX 변환에 실패했습니다.");
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    }, [tabActive]);

    const copyToClipboard = () => {
        if (bibtexTextareaRef.current) {
            const bib = bibtexTextareaRef.current.value;

            navigator.clipboard
                .writeText(bib)
                .then(() => {
                    setToast("복사되었습니다", "info");
                })
                .catch((error) => {
                    console.error("복사 실패:", error);
                });
        }
    };

    // region: 외부 저장 핸들러
    useImperativeHandle(ref, () => ({
        bibJsonMutate, // 외부에서 호출할 수 있도록 expose
        getCurrentBibJson: () => bibJson,
    }));

    return (
        <Container>
            <TabSection>
                <TabButton onClick={() => setTabActive("form")} active={tabActive === "form"}>
                    <TabButtonText active={tabActive === "form"}>Form</TabButtonText>
                </TabButton>
                <TabButton onClick={() => setTabActive("bibtex")} active={tabActive === "bibtex"}>
                    <TabButtonText active={tabActive === "bibtex"}>BibTeX</TabButtonText>
                </TabButton>
            </TabSection>
            <TabViewSection onSubmit={() => {}}>
                <FormContainer key={uuidv4()} active={tabActive === "form"}>
                    <FormInner>
                        {Object.entries(bibJson).map(([field, value]) => {
                            if (field === "entry_type") {
                                return (
                                    <FormRow key={uuidv4()}>
                                        <ItemLeft>
                                            <LabelSign />
                                            <FormLabelWrap>
                                                <FormLabel>
                                                    {field} {" *"}
                                                </FormLabel>
                                            </FormLabelWrap>
                                        </ItemLeft>
                                        <ItemRight>
                                            <FormContent>
                                                <WithDelBtn>
                                                    <SelectBox
                                                        height={"24px"}
                                                        width={"calc(100% - 32px)"}
                                                        optionWidth="100%"
                                                        value={value}
                                                        topBottom={"top"}
                                                        top={"24px"}
                                                        textAlign={"left"}
                                                        items={Object.keys(Constants.BIBTEX_FIELD).map((key) => {
                                                            return {
                                                                id: key,
                                                                name: key,
                                                            };
                                                        })}
                                                        onChange={(value) => entryTypeChange(value)}
                                                    />
                                                </WithDelBtn>
                                            </FormContent>
                                        </ItemRight>
                                    </FormRow>
                                );
                            }

                            if (field === "abstract") return null;

                            const fieldList = Constants.BIBTEX_FIELD[bibJson.entry_type];
                            const isDeletedList = [];
                            Object.keys(fieldList).forEach((key) => {
                                if (!bibJson[key]) {
                                    isDeletedList.push(key);
                                }
                            });

                            const options = {
                                value: value,
                                shouldUnregister: fieldList[field] !== "required",
                            };
                            if (Constants.BIBTEX_FIELD_TYPE[field] === "multiple") {
                                const fieldValues = value?.split(" and ") || [];

                                return (
                                    <div key={uuidv4()}>
                                        {fieldValues.map((fieldValue, index) => {
                                            return (
                                                <FormRow key={uuidv4()}>
                                                    <ItemLeft>
                                                        {index === 0 && (
                                                            <>
                                                                <LabelSign />
                                                                <FormLabelWrap>
                                                                    <FormLabel>
                                                                        {field}
                                                                        {fieldList[field] === "required" && " *"}
                                                                    </FormLabel>
                                                                </FormLabelWrap>
                                                            </>
                                                        )}
                                                    </ItemLeft>
                                                    <ItemRight>
                                                        <FormContent>
                                                            <WithDelBtn>
                                                                <FormInput
                                                                    {...register(`${field}[${index}]`, {
                                                                        value: fieldValue,
                                                                        shouldUnregister: true,
                                                                    })}
                                                                    hasError={!!error[field]}
                                                                    onBlur={(e) =>
                                                                        handleUpdate({
                                                                            ...bibJson,
                                                                            [field]: fieldValues
                                                                                .map((v, i) =>
                                                                                    i === index ? e.target.value : v,
                                                                                )
                                                                                .join(" and "),
                                                                        })
                                                                    }
                                                                    onKeyDown={handleOnKeyDown}
                                                                />
                                                                {index === fieldValues.length - 1 && (
                                                                    <Button
                                                                        onlyIcon
                                                                        margin={"0 0 0 8px"}
                                                                        buttonSize={"24px"}
                                                                        bgColor={"transparent"}
                                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                                        onClick={(e) => {
                                                                            const newBibJson = { ...bibJson };
                                                                            newBibJson[field] =
                                                                                newBibJson[field] + " and ";
                                                                            handleUpdate(newBibJson);
                                                                        }}
                                                                    >
                                                                        <Icon
                                                                            name={"plus"}
                                                                            size={"12"}
                                                                            color={"var(--color-SubBlack)"}
                                                                        />
                                                                    </Button>
                                                                )}
                                                                {!(
                                                                    fieldList[field] === "required" &&
                                                                    fieldValues.length === 1
                                                                ) && (
                                                                    <Button
                                                                        onlyIcon
                                                                        margin={"0 0 0 8px"}
                                                                        buttonSize={"24px"}
                                                                        bgColor={"transparent"}
                                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                                        onClick={(e) => {
                                                                            const newBibJson = { ...bibJson };
                                                                            if (fieldValues.length === 1) {
                                                                                fieldDelete(field);
                                                                                return;
                                                                            }
                                                                            newBibJson[field] = fieldValues
                                                                                .filter((_, i) => i !== index)
                                                                                .join(" and ");
                                                                            handleUpdate(newBibJson);
                                                                            unregister(`${field}[${index}]`);
                                                                        }}
                                                                    >
                                                                        <Icon
                                                                            name={"delete"}
                                                                            size={"12"}
                                                                            color={"var(--color-SubBlack)"}
                                                                        />
                                                                    </Button>
                                                                )}
                                                            </WithDelBtn>
                                                        </FormContent>
                                                    </ItemRight>
                                                </FormRow>
                                            );
                                        })}
                                        {error[field] && <ErrorMessage>{error[field]}</ErrorMessage>}
                                    </div>
                                );
                            }
                            return (
                                <div key={uuidv4()}>
                                    <FormRow>
                                        <ItemLeft>
                                            <LabelSign />
                                            <FormLabelWrap>
                                                <FormLabel>
                                                    {field}
                                                    {fieldList[field] === "required" && " *"}
                                                </FormLabel>
                                            </FormLabelWrap>
                                        </ItemLeft>
                                        <ItemRight>
                                            <FormContent>
                                                <WithDelBtn>
                                                    <FormInput
                                                        {...register(`${field}`, options)}
                                                        hasError={!!error[field]}
                                                        onBlur={(e) =>
                                                            handleUpdate({
                                                                ...bibJson,
                                                                [field]: e.target.value,
                                                            })
                                                        }
                                                        onKeyDown={handleOnKeyDown}
                                                    />
                                                    <Button
                                                        key={uuidv4()}
                                                        onlyIcon
                                                        margin={"0 0 0 8px"}
                                                        buttonSize={"24px"}
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={(e) => fieldDelete(field)}
                                                        style={
                                                            fieldList[field] === "required" && {
                                                                visibility: "hidden",
                                                            }
                                                        }
                                                    >
                                                        <Icon
                                                            name={"delete"}
                                                            size={"12"}
                                                            color={"var(--color-SubBlack)"}
                                                        />
                                                    </Button>
                                                </WithDelBtn>
                                            </FormContent>
                                        </ItemRight>
                                    </FormRow>
                                    {error[field] && <ErrorMessage>{error[field]}</ErrorMessage>}
                                </div>
                            );
                        })}
                    </FormInner>
                    {/* 필드 추가 영역 */}
                    {deletedField.length !== 0 && (
                        <FieldAddArea>
                            {deletedField.sort().map((field) => {
                                return (
                                    <FieldAddButton
                                        key={uuidv4()}
                                        buttonText={field}
                                        onClick={(e) => {
                                            const newBibJson = { ...bibJson };
                                            newBibJson[field] = "";
                                            handleUpdate(newBibJson);
                                        }}
                                    >
                                        <Icon name={"plus"} size={"12"} color={"var(--color-White)"} />
                                    </FieldAddButton>
                                );
                            })}
                        </FieldAddArea>
                    )}
                </FormContainer>
                <BibTeXContainer active={tabActive === "bibtex"}>
                    <BibTexTextarea autoFocus ref={bibtexTextareaRef} value={bibtexValue} readOnly />
                    <BibTexButtonColumn className="buttonWrap">
                        <Tooltip message={"복사"}>
                            <Button
                                onlyIcon
                                bgColor={"rgba(30, 30, 30, 0.8)"}
                                hoverBgColor={"var(--color-Key)"}
                                onClick={copyToClipboard}
                            >
                                <Icon name={"copy"} size={"12"} color={"var(--color-White)"} />
                            </Button>
                        </Tooltip>
                        <Tooltip message={"다운로드"}>
                            <Button
                                onlyIcon
                                bgColor={"rgba(30, 30, 30, 0.8)"}
                                hoverBgColor={"var(--color-Key)"}
                                onClick={(e) =>
                                    saveAs(new Blob([bibtexValue]), `paper-${new Date().toISOString()}.bib`)
                                }
                            >
                                <Icon name={"download"} size={"12"} color={"var(--color-White)"} />
                            </Button>
                        </Tooltip>
                    </BibTexButtonColumn>
                </BibTeXContainer>
            </TabViewSection>
        </Container>
    );
}

export default React.forwardRef(TabReferenceBibInfo);
